import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { authApi, setPrincipal } from '@/api';
import { ApiError, USER_KEY, setUnauthorizedHandler } from '@/api/client';
import type { Role } from '@/api/types';
import { setActiveCurrency, normalizePhone } from '@/lib/format';
import { sanitizeText } from '@/lib/validation';

export type AuthUser = {
  userName: string;
  phone: string;
  roles: Role[];
  buildingId: number | null;
  buildingPublicId: string | null;
  /** عملة العمارة. اختيارية لأن جلسة الأدمن بلا عمارة. */
  currency?: string;
  currencyMinorUnits?: number;
  buildingIds: number[];
  expiresAtUtc: string;
  /** يأتي من `/auth/me` في كل تحميل، فلا يتخطّاه المستخدم بتحديث الصفحة. */
  mustChangePassword?: boolean;
  /**
   * شقة الساكن في عمارته الأساسية (#47). `null` لمن لا شقة له.
   * صفحة الساكن تُفلتر بها؛ التخمين بالاسم كان يعرض رصيد الجار.
   */
  unitId: number | null;
  unitNumber: string | null;
};

export type AuthContextValue = {
  userName: string | null;
  phone: string | null;
  roles: Role[];
  buildingId: number | null;
  buildingPublicId: string | null;
  /** عملة العمارة. اختيارية لأن جلسة الأدمن بلا عمارة. */
  currency?: string;
  currencyMinorUnits?: number;
  /** Buildings this principal may read. Empty array means "all" (admin). */
  buildingIds: number[];
  /** شقة الساكن المربوطة بحسابه، أو `null` — لا تخمين (#47). */
  unitId: number | null;
  unitNumber: string | null;
  isAuthenticated: boolean;
  /** مالك المنصّة: كل شيء بلا استثناء. */
  isAdmin: boolean;
  /** أدمن أو محرّر — من يعمل على مستوى المنصّة لا داخل عمارة. */
  isPlatform: boolean;
  /** محرّر فقط: يدير العمارات والمستخدمين، ولا يمسّ التسعير ولا الهوية. */
  isEditor: boolean;
  isManager: boolean;
  isResident: boolean;
  requestOtp: (phone: string) => Promise<void>;
  verifyOtp: (phone: string, code: string) => Promise<void>;
  /** كلمة سر ضبطها غير صاحبها — الحارس يحجز المستخدم في `/account`. */
  mustChangePassword: boolean;
  logout: () => void;
  /** Re-reads the principal from the server after the user edits their profile. */
  refresh: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const VALID_ROLES: Role[] = ['admin', 'editor', 'manager', 'resident'];
const MAX_TIMEOUT_MS = 2_147_483_000;

/**
 * Stored session state is attacker-controlled — anyone can edit localStorage —
 * so it is parsed defensively and validated field by field. A malformed,
 * inconsistent, or expired blob is discarded rather than trusted.
 */
function readStoredUser(): AuthUser | null {
  const raw = localStorage.getItem(USER_KEY);
  if (!raw || raw.length > 4096) return null;

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }

  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;
  const value = parsed as Record<string, unknown>;

  const roles = Array.isArray(value.roles)
    ? value.roles.filter((role): role is Role => VALID_ROLES.includes(role as Role))
    : [];
  if (roles.length === 0) return null;

  const buildingId =
    typeof value.buildingId === 'number' &&
    Number.isSafeInteger(value.buildingId) &&
    value.buildingId > 0
      ? value.buildingId
      : null;

  const buildingIds = Array.isArray(value.buildingIds)
    ? value.buildingIds.filter(
        (id): id is number => typeof id === 'number' && Number.isSafeInteger(id) && id > 0,
      )
    : [];

  // الشقة كالعمارة: عدد صحيح موجب أو لا شيء. تزويرها في localStorage لا
  // يفتح شيئاً — الخادم يُفلتر ما يراه الساكن على أي حال — لكنه يعرض له
  // بطاقة فارغة، فتُطهَّر هنا كباقي الحقول.
  const unitId =
    typeof value.unitId === 'number' && Number.isSafeInteger(value.unitId) && value.unitId > 0
      ? value.unitId
      : null;
  const unitNumber =
    unitId !== null && typeof value.unitNumber === 'string'
      ? sanitizeText(value.unitNumber, 10) || null
      : null;

  const expiresAtUtc = typeof value.expiresAtUtc === 'string' ? value.expiresAtUtc : '';
  const expiry = new Date(expiresAtUtc).getTime();
  if (!Number.isFinite(expiry) || expiry <= Date.now()) return null;

  // A non-admin session without a building is nonsensical: reject it.
  if (!roles.includes('admin') && buildingId === null) return null;

  return {
    userName: sanitizeText(value.userName, 80) || '—',
    phone: typeof value.phone === 'string' ? normalizePhone(value.phone) : '',
    roles,
    buildingId,
    buildingPublicId:
      typeof value.buildingPublicId === 'string' ? sanitizeText(value.buildingPublicId, 40) : null,
    /* عملة العمارة تأتي مع الجلسة: كل مبلغ على الشاشة يحتاجها، ونداء
       ثانٍ يعني أن أول عرض للأرقام يكون بعملة خاطئة ثم يُصحَّح. */
    currency:
      typeof value.currency === 'string' && /^[A-Za-z]{3}$/.test(value.currency)
        ? value.currency.toUpperCase()
        : 'JOD',
    currencyMinorUnits:
      typeof value.currencyMinorUnits === 'number'
        && value.currencyMinorUnits >= 0
        && value.currencyMinorUnits <= 3
        ? value.currencyMinorUnits
        : 3,
    buildingIds,
    expiresAtUtc,
    /*
     * العلم يُقرأ من المخزون كذلك.
     *
     * كان يسقط هنا، فيبقى الحارس صامتاً حتى يعود `/auth/me` — وهي فجوة
     * يكفيها تحديث للصفحة. وتمريره آمن لأن اتجاهه واحد: تزويره في
     * localStorage يحبس المزوّر في صفحة تغيير كلمة السر، ولا يفتح له شيئاً.
     * والخادم هو من يرفعه في كل تحميل على أي حال.
     */
    mustChangePassword: value.mustChangePassword === true,
    unitId,
    unitNumber,
  };
}

function clearSession(): void {
  localStorage.removeItem(USER_KEY);
}

/** Mirrors the session into the API authorization layer. */
function syncPrincipal(user: AuthUser | null): void {
  setPrincipal(
    user ? { roles: user.roles, buildingId: user.buildingId, buildingIds: user.buildingIds } : null,
  );
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(() => {
    const stored = readStoredUser();
    if (!stored) clearSession();
    // Synchronous on purpose: child effects run before parent effects, so a
    // provider below this one would otherwise call the API unauthenticated.
    syncPrincipal(stored);
    return stored;
  });

  // Safety net for any path that sets state without going through the helpers.
  useEffect(() => {
    syncPrincipal(user);
    /*
      العملة تُضبط مع الجلسة لا عند كل عرض.
      ------------------------------------
      `formatMoney` يُنادى من نحو مئة موضع؛ تمرير العملة إليها جميعاً يعني
      أن أيّ موضع يُنسى يعرض مبلغاً بعملة عمارة أخرى — وهو خطأ لا يُرى
      لأنه لا يُعطّل شيئاً، ويُكتشف من عميل غاضب.
    */
    setActiveCurrency(user?.currency ?? 'JOD', user?.currencyMinorUnits ?? 3);
  }, [user]);

  /**
   * The cached user is display state, not proof of a session. On every load we
   * ask the server who we actually are; the cookie is the only thing that can
   * answer. A 401 means the cookie is gone or revoked, so the cache goes too.
   */
  useEffect(() => {
    let cancelled = false;
    if (!localStorage.getItem(USER_KEY)) return;

    void (async () => {
      try {
        const me = await authApi.me();
        if (cancelled) return;
        const fresh: AuthUser = {
          userName: me.userName,
          phone: me.phone,
          roles: me.roles,
          buildingId: me.buildingId,
          buildingPublicId: me.buildingPublicId,
          buildingIds: me.buildingIds,
          expiresAtUtc: me.expiresAtUtc,
          mustChangePassword: me.mustChangePassword,
          // كانت العملة تسقط هنا فتعود إلى الدينار بعد أول تحميل — نفس
          // الحقل، نفس الموضع، نفس الدرس: كل حقل جلسة يُنسخ في المواضع الثلاثة.
          currency: me.currency,
          currencyMinorUnits: me.currencyMinorUnits,
          unitId: me.unitId ?? null,
          unitNumber: me.unitNumber ?? null,
        };
        localStorage.setItem(USER_KEY, JSON.stringify(fresh));
        syncPrincipal(fresh);
        setUser(fresh);
      } catch (error) {
        if (cancelled) return;
        // Network blips must not sign anyone out; only an explicit 401 does.
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          clearSession();
          syncPrincipal(null);
          setUser(null);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Any 401 from any endpoint drops the session once, centrally.
  useEffect(() => {
    setUnauthorizedHandler(() => {
      clearSession();
      syncPrincipal(null);
      setUser(null);
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  // Expire the session in-tab the moment it lapses, without waiting for a reload.
  useEffect(() => {
    if (!user) return;
    const remaining = new Date(user.expiresAtUtc).getTime() - Date.now();
    if (remaining <= 0) {
      clearSession();
      syncPrincipal(null);
      setUser(null);
      return;
    }
    const timer = setTimeout(() => {
      clearSession();
      syncPrincipal(null);
      setUser(null);
    }, Math.min(remaining, MAX_TIMEOUT_MS));
    return () => clearTimeout(timer);
  }, [user]);

  // Signing out in one tab signs out every tab.
  useEffect(() => {
    const onStorage = (event: StorageEvent) => {
      if (event.key === USER_KEY && event.newValue === null) {
        syncPrincipal(null);
        setUser(null);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const requestOtp = useCallback(async (phone: string) => {
    await authApi.requestOtp(normalizePhone(phone));
  }, []);

  const verifyOtp = useCallback(async (phone: string, code: string) => {
    const response = await authApi.verifyOtp(normalizePhone(phone), code);
    const nextUser: AuthUser = {
      userName: response.userName,
      phone: response.phone,
      roles: response.roles,
      buildingId: response.buildingId,
      buildingPublicId: response.buildingPublicId,
      buildingIds: response.buildingIds,
      expiresAtUtc: response.expiresAtUtc,
      currency: response.currency,
      currencyMinorUnits: response.currencyMinorUnits,
      unitId: response.unitId ?? null,
      unitNumber: response.unitNumber ?? null,
    };
    localStorage.setItem(USER_KEY, JSON.stringify(nextUser));
    syncPrincipal(nextUser);
    setUser(nextUser);
  }, []);

  /**
   * Pulls a fresh principal. Called after a self-service edit: the header and
   * sidebar render from the cached user, so without this a renamed user keeps
   * seeing their old name until the next full page load.
   */
  const refresh = useCallback(async () => {
    try {
      const me = await authApi.me();
      const fresh: AuthUser = {
        userName: me.userName,
        phone: me.phone,
        roles: me.roles,
        buildingId: me.buildingId,
        buildingPublicId: me.buildingPublicId,
        buildingIds: me.buildingIds,
        expiresAtUtc: me.expiresAtUtc,
        mustChangePassword: me.mustChangePassword,
        currency: me.currency,
        currencyMinorUnits: me.currencyMinorUnits,
        unitId: me.unitId ?? null,
        unitNumber: me.unitNumber ?? null,
      };
      localStorage.setItem(USER_KEY, JSON.stringify(fresh));
      syncPrincipal(fresh);
      setUser(fresh);
    } catch {
      // A failed refresh is not a reason to sign anyone out; the 401 handler
      // already covers the case where the session actually died.
    }
  }, []);

  const logout = useCallback(() => {
    // Fire-and-forget: the local session is dropped either way, but the server
    // needs the call to revoke the row and expire the cookie.
    void authApi.logout().catch(() => undefined);
    clearSession();
    syncPrincipal(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => {
    const roles = user?.roles ?? [];
    return {
      userName: user?.userName ?? null,
      phone: user?.phone ?? null,
      roles,
      buildingId: user?.buildingId ?? null,
      buildingPublicId: user?.buildingPublicId ?? null,
      buildingIds: user?.buildingIds ?? [],
      unitId: user?.unitId ?? null,
      unitNumber: user?.unitNumber ?? null,
      isAuthenticated: user !== null,
      mustChangePassword: Boolean(user?.mustChangePassword),
      isAdmin: roles.includes('admin'),
      isEditor: roles.includes('editor'),
      isPlatform: roles.includes('admin') || roles.includes('editor'),
      // المحرّر يعمل داخل العمارات كمسؤول، وهذا كل الغرض منه.
      isManager:
        roles.includes('manager') || roles.includes('admin') || roles.includes('editor'),
      isResident: roles.includes('resident'),
      requestOtp,
      verifyOtp,
      logout,
      refresh,
    };
  }, [user, requestOtp, verifyOtp, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

/**
 * Where each role belongs after signing in.
 *
 * الساكن إلى `/me` لا `/home` (#46): `/home` هي الصفحة التعريفية العامة،
 * وكان مسار الساكن معرَّفاً بالاسم نفسه داخل `RequireAuth`، والراوتر يأخذ
 * أول تطابق — فكان الساكن يسجّل دخوله ليرى صفحة التسويق. مسار واحد لكل
 * صفحة، واسم لا يشبه غيره.
 */
export function landingRoute(roles: Role[]): string {
  if (roles.includes('admin')) return '/admin';
  if (roles.includes('manager')) return '/dashboard';
  return '/me';
}
